import { useEffect, lazy, Suspense } from 'react';
import { initializeDefaultSettings } from '../services/settingsService';
import ErrorBoundary from './components/ErrorBoundary';
import { ConfirmHost } from './components/confirm';
import { watchSwUpdate, useSwUpdate } from './lib/swUpdate';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'sonner';
import './styles/fonts';
import './styles/tokens.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from './lib/useTheme';
import { useAuthUser } from './lib/useAuthUser';
import { useData } from './store/data';
import Shell from './layout/Shell';
import Overview from './features/overview/Overview';
import Transactions from './features/transactions/Transactions';
import EntrySheet from './features/entry/EntrySheet';
import AssetsDebts from './features/assets/AssetsDebts';
import Budget from './features/budget/Budget';
import Planned from './features/planned/Planned';
import Settings from './features/settings/Settings';
// Báo cáo kéo theo thư viện biểu đồ — nạp khi mở, không làm nặng các trang còn lại
const Reports = lazy(() => import('./features/reports/Reports'));
const Plan = lazy(() => import('./features/plan/Plan'));
import { useRecurringGenerator } from './features/budget/useRecurringGenerator';

/** Đường dẫn của bản cũ → trang tương ứng ở bản mới (giữ nguyên bookmark). */
const LEGACY_REDIRECTS: Record<string, string> = {
  '/': '/v2', '/assets': '/v2/tai-san', '/income': '/v2/giao-dich', '/expense': '/v2/giao-dich',
  '/analysis': '/v2/bao-cao', '/planning': '/v2/ke-hoach', '/automation': '/v2/du-kien',
  '/data': '/v2/cai-dat', '/settings': '/v2/cai-dat'
};

/**
 * FinTrack 2.0 — gốc ứng dụng v2, gắn ở /v2/*. Tự lo đăng nhập (không dùng các
 * listener của bản cũ) và dùng CHUNG dữ liệu Firebase với bản cũ.
 */
export default function V2Root() {
  const { pathname } = useLocation();
  const { theme, toggle } = useTheme();
  const { user, loading, error, signIn, signOut } = useAuthUser();
  const start = useData(s => s.start);

  useEffect(() => (user ? start(user.uid) : undefined), [user, start]);
  useRecurringGenerator(user?.uid);
  const setUpdate = useSwUpdate(s => s.set);
  useEffect(() => { if (import.meta.env.PROD) void watchSwUpdate(navigator.serviceWorker as any, activate => setUpdate(activate)); }, [setUpdate]);

  // Người dùng MỚI (cả hai bộ danh mục hoàn toàn trống) mới cần danh mục mặc định.
  // KHÔNG gọi cho người dùng cũ: initializeDefaultSettings GHI ĐÈ danh mục mặc định.
  const catsReady = useData(s => s.ready.expenseCategories && s.ready.incomeCategories);
  const noCats = useData(s => s.data.expenseCategories.length === 0 && s.data.incomeCategories.length === 0);
  useEffect(() => { if (user && catsReady && noCats) initializeDefaultSettings(user.uid); }, [user, catsReady, noCats]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" aria-label="Đang tải" /></div>;
  }
  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardContent className="space-y-4 p-6 text-center">
            <div className="flex items-center justify-center gap-2 text-xl font-semibold tracking-tight">
              <span className="h-2.5 w-2.5 rounded-full bg-primary" aria-hidden /> FinTrack
            </div>
            <p className="text-sm text-muted-foreground">Đăng nhập để xem tài chính của bạn.</p>
            {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            <Button className="w-full" onClick={signIn}>Đăng nhập bằng Google</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <Toaster theme={theme} position="bottom-center" richColors closeButton />
    <EntrySheet />
    <ConfirmHost />
    <Routes>
      <Route path="/v2" element={<Shell theme={theme} onToggleTheme={toggle} onSignOut={signOut} userName={user.displayName ?? user.email} />}>
        <Route index element={<Overview />} />
        <Route path="du-kien" element={<Planned />} />
        <Route path="giao-dich" element={<Transactions />} />
        <Route path="tai-san" element={<AssetsDebts />} />
        <Route path="ngan-sach" element={<Budget />} />
        <Route path="cai-dat" element={<Settings theme={theme} onToggleTheme={toggle} />} />
        <Route path="ke-hoach" element={<Suspense fallback={<div className="p-8 text-center text-sm text-muted-foreground">Đang tải kế hoạch…</div>}><Plan /></Suspense>} />
        <Route path="bao-cao" element={<Suspense fallback={<div className="p-8 text-center text-sm text-muted-foreground">Đang tải báo cáo…</div>}><Reports /></Suspense>} />
      </Route>
      <Route path="*" element={<Navigate to={LEGACY_REDIRECTS[pathname] ?? '/v2'} replace />} />
    </Routes>
    </ErrorBoundary>
  );
}
